/**
 * The event envelope: the generic wrapper every recorded fact travels in.
 *
 * Products define their own event types and payloads; core only knows the envelope.
 * Events are created on the device (often offline), so:
 * - `id` is generated on the device. Syncing the same event twice is harmless (dedupe by id).
 * - `deviceId` + `deviceSeq` give a reliable order within one device, independent of clock drift.
 * - `schemaVersion` belongs to the EVENT, not the app. A phone running last month's build can
 *   sync old-shaped events, and the server upgrades them in one place (`createEventUpgrader`).
 */

export interface EventEnvelope<TType extends string = string, TPayload = unknown> {
  readonly id: string;
  readonly type: TType;
  readonly schemaVersion: number;
  /** ISO-8601 instant with offset, from the device clock. Used for display and as a tie-breaker. */
  readonly occurredAt: string;
  readonly recordedBy: string;
  readonly deviceId: string;
  /** Monotonic per device: 1, 2, 3, … */
  readonly deviceSeq: number;
  readonly payload: TPayload;
}

/** Upgrades a payload from version N to N+1. */
export type PayloadMigration = (payload: unknown) => unknown;

export interface EventUpgrader {
  /**
   * Bring an event to the current schema version for its type.
   * Returns `{ ok: false }` instead of throwing for unknown types or versions, so one
   * unreadable event never blocks a sync. The caller records it as unreadable.
   */
  upgrade(event: EventEnvelope): { ok: true; event: EventEnvelope } | { ok: false; reason: string };
}

/**
 * @param current     current schema version per event type, e.g. `{ "pot.placed": 2 }`
 * @param migrations  `{ "pot.placed": { 1: (v1) => v2 } }`: one step per version bump
 */
export function createEventUpgrader(
  current: Readonly<Record<string, number>>,
  migrations: Readonly<Record<string, Readonly<Record<number, PayloadMigration>>>> = {},
): EventUpgrader {
  return {
    upgrade(event) {
      const target = current[event.type];
      if (target === undefined) return { ok: false, reason: `unknown event type "${event.type}"` };
      if (event.schemaVersion > target) {
        return { ok: false, reason: `"${event.type}" v${event.schemaVersion} is newer than this build (v${target})` };
      }
      let payload = event.payload;
      for (let version = event.schemaVersion; version < target; version++) {
        const step = migrations[event.type]?.[version];
        if (!step) return { ok: false, reason: `no migration for "${event.type}" v${version} → v${version + 1}` };
        payload = step(payload);
      }
      return { ok: true, event: { ...event, schemaVersion: target, payload } };
    },
  };
}
